import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Aivionreach.css';
import { navigateToHomeSection } from '../utils/navigation';

const journey = [
  {
    id: '01',
    title: 'Understand',
    short: 'Discover your strengths, interests and career goals.',
    detail: 'Build a clearer picture of what motivates you, the strengths you already have and the kinds of roles that could be a good fit.'
  },
  {
    id: '02',
    title: 'Prepare',
    short: 'Turn your goals into a practical personal plan.',
    detail: 'Create a focused action plan, organise your next steps and get ready for the opportunities you want to pursue.'
  },
  {
    id: '03',
    title: 'Develop',
    short: 'Build the skills employers are looking for.',
    detail: 'Identify skill gaps, find relevant learning and record your progress as you develop the capabilities needed for your chosen path.'
  },
  {
    id: '04',
    title: 'Demonstrate',
    short: 'Show your strengths with evidence and confidence.',
    detail: 'Capture achievements, projects and experiences in a personal profile that helps employers understand what you can do.'
  },
  {
    id: '05',
    title: 'Apply',
    short: 'Create stronger, tailored job applications.',
    detail: 'Use your profile and target role to prepare relevant CVs, cover letters and application answers while keeping your own voice.'
  },
  {
    id: '06',
    title: 'Connect',
    short: 'Explore opportunities and prepare to connect with employers.',
    detail: 'Explore potentially suitable roles, prepare for conversations and approach employers with a clearer picture of your potential.'
  },
  {
    id: '07',
    title: 'Track',
    short: 'Keep every action and application in one place.',
    detail: 'Follow your progress, manage deadlines and see the steps you have completed so you always know what to do next.'
  }
];

const outcomes = [
  'A clearer direction based on your goals, strengths and interests',
  'A practical plan for building the skills your chosen roles require',
  'Stronger evidence of your achievements and potential',
  'Tailored applications that remain authentic to you',
  'One place to manage opportunities, actions and progress'
];

const faqs = [
  {
    question: 'What is aivionReach?',
    answer: 'aivionReach is an AI-supported employment platform that helps you move from exploring career goals to building skills, applying for roles, connecting with employers and tracking your progress.'
  },
  {
    question: 'How does AI support my job search?',
    answer: 'AI helps you organise your ideas, recognise relevant strengths, explore suitable pathways and tailor application materials. You stay in control of every decision and everything you submit.'
  },
  {
    question: 'Can I use aivionReach if I am unsure what career I want?',
    answer: 'Yes. The journey begins by helping you understand your interests, strengths and goals before you choose a pathway or start applying.'
  },
  {
    question: 'Does aivionReach apply for jobs for me?',
    answer: 'No. It helps you find, prepare for and manage opportunities, but you review and control each application.'
  },
  {
    question: 'Can I track more than one opportunity?',
    answer: 'Yes. You can keep your actions, applications, deadlines and progress together, making it easier to stay organised across multiple opportunities.'
  }
];

const products = [
  { name: 'aivionGrade', tag: 'AI-Powered Exam Assessment', link: 'https://aiviongrade.aivion.ai/' },
  { name: 'aivionScore', tag: 'AI-Based Credit Review', link: '/aivionscore' },
  { name: 'aivionSales', tag: 'AI-Based Sales & CRM', link: '/aivionsales' }
];

const navLinks = [
  { label: 'Services', href: '/#services' },
  { label: 'Products', href: '/#products' },
  { label: 'Industries', href: '/#industries' },
  { label: 'Partners', href: '/#clients' },
  { label: 'About', href: '/#about' },
  { label: 'How We Work', href: '/#how-we-work' },
  { label: 'Case Studies', href: '/#case-studies' },
  { label: 'Contact Us', href: '/#contact' }
];

export default function AivionReach() {
  const [active, setActive] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  const goHome = (event, href) => {
    event.preventDefault();
    navigateToHomeSection(href);
  };

  return (
    <div className="ar-root">
      <nav className="ar-nav">
        <div className="ar-nav-inner">
          <div className="ar-logo-box"><span>AIV</span><span className="ar-green">ION</span></div>
          <ul className="ar-nav-links">
            {navLinks.map((link) => (
              <li key={link.label}><a href={link.href} onClick={(event) => goHome(event, link.href)}>{link.label}</a></li>
            ))}
          </ul>
          <a href="/#contact" className="ar-contact-btn" onClick={(event) => goHome(event, '/#contact')}>CONTACT US</a>
        </div>
      </nav>

      <section className="ar-hero-wrap">
        <div className="ar-hero">
          <div className="ar-hero-copy">
            <a className="ar-breadcrumb" href="/#products" onClick={(event) => goHome(event, '/#products')}>
              <span aria-hidden="true">&#8592;</span> AIVION PRODUCTS
            </a>
            <h1>aivionReach</h1>
            <p className="ar-hero-subtitle">AI-Supported Employment Platform</p>
            <p className="ar-hero-intro">A guided journey to help you prepare for employment.</p>
            <div className="ar-hero-actions">
              <a href="https://aivionreach.aivion.ai/" target="_blank" rel="noreferrer" className="ar-button ar-button-primary">START YOUR JOURNEY &#8594;</a>
              <a href="#journey" className="ar-button ar-button-ghost">EXPLORE THE JOURNEY</a>
            </div>
          </div>
          <div className="ar-journey-preview" aria-label="The aivionReach journey">
            {journey.map((step, index) => (
              <div className="ar-preview-step" key={step.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{step.title}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ar-built">
        <div className="ar-built-inner">
          <div>
            <p className="ar-eyebrow">THE PRODUCT</p>
            <h2>
              One journey.<br />
              <span className="ar-nowrap">With you every step.</span>
            </h2>
          </div>
          <p className="ar-built-copy">aivionReach helps you understand where you want to go and take practical steps to get there. Explore career goals, build relevant skills, demonstrate what you can do, prepare tailored applications, connect with employers and keep your progress visible from one place.</p>
        </div>
      </section>

      <section className="ar-tour" id="journey">
        <div className="ar-section-heading">
          <div><p className="ar-eyebrow">YOUR JOURNEY</p><h2>Seven steps to move forward</h2></div>
          <p>Select a stage to see how aivionReach supports your next move.</p>
        </div>
        <div className="ar-tour-body">
          <div className="ar-step-list" role="tablist" aria-label="Job seeker journey">
            {journey.map((step, index) => (
              <button
                key={step.id}
                className={`ar-step${active === index ? ' ar-step-active' : ''}`}
                onClick={() => setActive(index)}
                role="tab"
                aria-selected={active === index}
              >
                <span>{step.id}</span>
                <span className="ar-step-copy"><strong>{step.title}</strong><small>{step.short}</small></span>
                {active === index && <b aria-hidden="true">&#8594;</b>}
              </button>
            ))}
          </div>
          <div className="ar-step-detail" role="tabpanel">
            <p>STAGE {journey[active].id}</p>
            <h3>{journey[active].title}</h3>
            <strong>{journey[active].short}</strong>
            <span>{journey[active].detail}</span>
          </div>
        </div>
      </section>

      <section className="ar-outcomes">
        <div className="ar-outcomes-inner">
          <div><p className="ar-eyebrow">BUILT AROUND YOU</p><h2>Progress you can see and build on.</h2><p>A joined-up experience that turns career exploration into confident, manageable action.</p></div>
          <div className="ar-outcomes-grid">
            {outcomes.map((outcome) => <div key={outcome}><span>&#10003;</span><p>{outcome}</p></div>)}
          </div>
        </div>
      </section>

      <section className="ar-faq">
        <div className="ar-faq-inner">
          <div><p className="ar-eyebrow">FREQUENTLY ASKED QUESTIONS</p><h2>What you need to know</h2></div>
          <div className="ar-faq-list">
            {faqs.map((faq, index) => (
              <div className="ar-faq-item" key={faq.question}>
                <button onClick={() => setOpenFaq(openFaq === index ? -1 : index)} aria-expanded={openFaq === index}>
                  <span>{faq.question}</span><span>{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && <p>{faq.answer}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ar-more">
        <div className="ar-more-inner">
          <div className="ar-more-heading"><div><p className="ar-eyebrow">THE PRODUCT FAMILY</p><h2>More from AIVION</h2></div><a href="/#products" onClick={(event) => goHome(event, '/#products')}>All products &#8594;</a></div>
          <div className="ar-more-grid">
            {products.map((product) => product.link.startsWith('http') ? (
              <a key={product.name} href={product.link} target="_blank" rel="noreferrer" className="ar-product-card"><span>PRODUCT <b>&#8599;</b></span><h3>{product.name}</h3><p>{product.tag}</p></a>
            ) : (
              <Link key={product.name} to={product.link} className="ar-product-card"><span>PRODUCT <b>&#8599;</b></span><h3>{product.name}</h3><p>{product.tag}</p></Link>
            ))}
          </div>
        </div>
      </section>

      <section className="ar-cta">
        <div><p className="ar-eyebrow">READY FOR YOUR NEXT STEP?</p><h2>Start your journey with aivionReach.</h2><p>Bring your goals, experience and ambitions together—and turn them into a plan you can act on.</p></div>
        <a href="https://aivionreach.aivion.ai/" target="_blank" rel="noreferrer" className="ar-button ar-button-outline">GET STARTED &#8594;</a>
      </section>
    </div>
  );
}
